import type { FeedbackDto, IssueStatus, ReportsDto } from '@nsb/core';

/** The kind a report is listed under on Bugs and requests. */
export type ReportKind = 'bug' | 'feature' | 'comment';
export type ReportFilter = 'all' | ReportKind;

type Report = Pick<FeedbackDto, 'id' | 'kind' | 'issueType' | 'issueStatus'>;
type DoneCounts = ReportsDto['done'];

export const reportKind = (f: Pick<FeedbackDto, 'kind' | 'issueType'>): ReportKind => (f.kind === 'comment' ? 'comment' : f.issueType);

/** Done: a bug marked fixed or a feature marked shipped (both `fixed` underneath). A comment is never done. */
export const reportDone = (f: Pick<FeedbackDto, 'kind' | 'issueStatus'>): boolean => f.kind === 'issue' && f.issueStatus === 'fixed';

/**
 * Planned, the work queue (Jason, 2026-10-07: "the convention should be each thing has a planned switch"): a switch on
 * every report still open, bug or feature; a done one has none. Switched on it is planned, switched off it is received
 * (a feature: new) again.
 */
export const hasPlannedSwitch = (f: Pick<FeedbackDto, 'kind' | 'issueStatus'>): boolean => f.kind === 'issue' && !reportDone(f);
export const plannedStatus = (on: boolean): IssueStatus => (on ? 'planned' : 'received');

/**
 * What Bugs and requests lists (L-10, Jason, 2026-10-07: "bugs marked as fixed should should not be in this list"):
 * the reports still open, of the kind chosen, and the done ones as well only while Done is on. Each kind's count is
 * how many rows its button would show; Done's is how many of the kind chosen are done, listed or not (`doneOf`, the
 * API's count: it lists only the newest done ones), and `doneUnlisted` how many of those are left out of the list.
 */
export function reportList<T extends Report>(list: T[], filter: ReportFilter, showDone: boolean, doneOf?: DoneCounts) {
  const ofKind = (k: ReportFilter) => list.filter((f) => k === 'all' || reportKind(f) === k);
  const listed = (f: T) => showDone || !reportDone(f);
  const count = (k: ReportFilter) => ofKind(k).filter(listed).length;
  const doneListed = ofKind(filter).filter(reportDone).length;
  const doneInAll = doneOf ? (filter === 'all' ? doneOf.bug + doneOf.feature : filter === 'comment' ? 0 : doneOf[filter]) : doneListed;
  const done = Math.max(doneInAll, doneListed);
  return {
    shown: ofKind(filter).filter(listed),
    counts: { all: count('all'), bug: count('bug'), feature: count('feature'), comment: count('comment') } satisfies Record<ReportFilter, number>,
    done,
    doneUnlisted: done - doneListed,
  };
}

/** The report a link names (`/settings/reports#<id>`), from the address's hash; null when it names none. */
export function linkedReport(hash: string): string | null {
  const id = hash.replace(/^#/, '');
  if (!id) return null;
  try {
    return decodeURIComponent(id);
  } catch {
    return id;
  }
}

/** One report put in the list as the API now has it: marked done it leaves the list at once, marked open again it comes back. */
export function withReport<T extends { id: string }>(list: T[] | undefined, report: T): T[] | undefined {
  return list?.map((f) => (f.id === report.id ? report : f));
}

/**
 * Bugs and requests as the API now has one report, before it is read again: the report in its place, and the counts of
 * the done moved with it (marked fixed, one more done of its kind; marked open again, one fewer; retyped, moved over).
 */
export function withReportIn<T extends Report>(data: { reports: T[]; done: DoneCounts } | undefined, report: T): { reports: T[]; done: DoneCounts } | undefined {
  if (!data) return data;
  const before = data.reports.find((f) => f.id === report.id);
  if (!before) return data;
  const done = { ...data.done };
  if (reportDone(before) && before.issueType in done) done[before.issueType] = Math.max(0, done[before.issueType] - 1);
  if (reportDone(report) && report.issueType in done) done[report.issueType] += 1;
  return { reports: withReport(data.reports, report)!, done };
}
